/**
 * Get Organization Teams - SINGLE SOURCE OF TRUTH
 * 
 * 🚨 CRITICAL: Multi-tenant isolation with proper organization filtering
 * 
 * This utility provides safe access to teams within an organization.
 * Ensures no data leaks across different gym organizations.
 * 
 * Used for:
 * - Organization team lists
 * - Staff "All Teams" dashboards
 * - Team selection dropdowns
 * - Multi-tenant data isolation
 * - Team management features
 * 
 * @param organizationId - The organization ID
 * @param options - Optional filters and data inclusion
 * @returns Array of teams in the organization
 * 
 * @example
 * // ✅ CORRECT - Use this instead of direct teams table queries
 * import { getOrganizationTeams } from '@/utils/organizations/getOrganizationTeams';
 * const teams = await getOrganizationTeams(organizationId, { activeOnly: true });
 *
 * U2 (#766, plan docs/plans/2026-06-10-001-feat-staff-athlete-team-flow-plan.md):
 * added the opt-in `includeSeason` join so team pickers (e.g. the staff Add
 * Athlete modal's Team Assignment step) can show each team's season and filter
 * out completed-season (historical) teams via isHistoricalTeam without forking
 * a sibling teams query. NOTE: seasons.season_status is a computed TS field,
 * NOT a database column — only select real columns (end_date / archived_at are
 * sufficient for isCompletedSeason).
 */

import { supabase } from '@/utils/supabase/client';
import { createLogger } from '@/utils/logger/devLogger';
import { USASFDivision } from '@/types/usasf';

const logger = createLogger('OrganizationTeams');

/** Season columns selected by the opt-in `includeSeason` join (real DB columns only). */
export interface TeamSeasonInfo {
  id: string;
  name: string;
  start_date?: string | null;
  end_date?: string | null;
  archived_at?: string | null;
}

export interface Team {
  id: string;
  name: string;
  level?: string;
  age_division?: string;
  gender?: string;
  status?: string;
  organization_id: string;
  season_id?: string;
  program_id?: string;
  coach_id?: string;
  assistant_coach_ids?: string[];
  max_athletes?: number;
  usasf_division_id?: string;
  usasf_division?: USASFDivision;
  /** Whether families on this team must enroll in season automatic payments. */
  requires_season_auto_pay?: boolean;
  location_id?: string;
  /** Present only when `includeSeason: true` is requested. */
  season?: TeamSeasonInfo | null;
  created_at?: string;
  updated_at?: string;
}

export interface TeamWithCounts extends Team {
  athlete_count?: number;
  active_athlete_count?: number;
}

/**
 * Get all teams in an organization
 * 
 * 🚨 CRITICAL: Always filters by organization_id for multi-tenant isolation
 * 
 * @param organizationId - The organization ID
 * @param options - Optional filters and data inclusion
 * @returns Array of teams in the organization
 */
export async function getOrganizationTeams(
  organizationId: string,
  options?: {
    activeOnly?: boolean;
    seasonId?: string;
    programId?: string;
    includeCounts?: boolean;
    level?: string;
    ageDiv?: string;
    // 🆕 NEW: USASF Division inclusion
    includeUsasfDivision?: boolean;
    // 🆕 U2 (#766): join the team's season (real columns only — seasons.season_status
    // is computed in TS, not a DB column) so pickers can show season names and
    // filter historical teams via isHistoricalTeam(team, team.season).
    includeSeason?: boolean;
    // 🆕 U2 review (2026-06-11): ADDITIVE opt-in. By default this utility
    // swallows errors to [] — which makes a fetch FAILURE indistinguishable
    // from "this gym has zero teams". Callers that must tell those apart
    // (e.g. the staff Add Athlete team picker's error-vs-empty states) pass
    // throwOnError: true to get the original error rethrown. Existing callers
    // are unchanged.
    throwOnError?: boolean;
    // 🆕 NEW: Location permission enforcement
    enforceLocationPermissions?: boolean;
    staffLocationInfo?: {
      locationIds: string[];
      hasAllLocationsAccess: boolean;
    };
    authUserId?: string; // For team assignment override
  }
): Promise<Team[] | TeamWithCounts[]> {
  try {
    logger.info('Fetching teams in organization:', { organizationId, options });

    const selectFields = `
      *,
      ${options?.includeCounts ? 'team_athletes!left(count)' : ''}
      ${options?.includeUsasfDivision ? 'usasf_division:usasf_divisions(*)' : ''}
      ${options?.includeSeason ? 'season:seasons(id, name, start_date, end_date, archived_at)' : ''}
    `.split('\n').map(line => line.trim()).filter(Boolean).join(',').replace(/,+/g, ',');

    let query = supabase
      .from('teams')
      .select(selectFields.endsWith(',') ? selectFields.slice(0, -1) : selectFields)
      .eq('organization_id', organizationId); // 🚨 CRITICAL: Multi-tenant isolation

    // Apply status filter
    if (options?.activeOnly !== false) {
      query = query.eq('status', 'active');
    }

    // Apply season filter
    if (options?.seasonId) {
      query = query.eq('season_id', options.seasonId);
    }

    // Apply program filter
    if (options?.programId) {
      query = query.eq('program_id', options.programId);
    }

    // Apply level filter
    if (options?.level) {
      query = query.eq('level', options.level);
    }

    // Apply age division filter
    if (options?.ageDiv) {
      query = query.eq('age_division', options.ageDiv);
    }

    // 🆕 NEW: Apply location filtering if requested
    if (options?.enforceLocationPermissions && options.staffLocationInfo) {
      const { hasAllLocationsAccess, locationIds } = options.staffLocationInfo;
      
      if (!hasAllLocationsAccess && locationIds.length > 0) {
        logger.info('Applying location filtering to teams', { 
          locationCount: locationIds.length,
          locationIds 
        });
        
        // Apply basic location filtering first
        query = query.in('location_id', locationIds);
        
        // TODO: Team assignment override will be handled by a separate query  
        // since Supabase doesn't support complex OR conditions with joins easily
      }
    }

    // Order by name
    query = query.order('name', { ascending: true });

    const { data, error } = await query;

    if (error) {
      logger.error('Error fetching organization teams:', error);
      throw error;
    }

    if (!data || data.length === 0) {
      logger.info('No teams found in organization:', { organizationId });
      return [];
    }

    let teams: Team[] | TeamWithCounts[] = data;

    // Add athlete counts if requested
    if (options?.includeCounts) {
      const teamsWithCounts: TeamWithCounts[] = await Promise.all(
        data.map(async (team: Team) => {
          // Get athlete counts for this team
          const { count: totalCount } = await supabase
            .from('team_athletes')
            .select('*', { count: 'exact', head: true })
            .eq('team_id', team.id);

          const { count: activeCount } = await supabase
            .from('team_athletes')
            .select('*', { count: 'exact', head: true })
            .eq('team_id', team.id)
            .eq('status', 'active');

          return {
            ...team,
            athlete_count: totalCount || 0,
            active_athlete_count: activeCount || 0
          };
        })
      );

      teams = teamsWithCounts;
    }

    logger.info(`Found ${teams.length} teams in organization`, { 
      organizationId,
      activeOnly: options?.activeOnly,
      teamCount: teams.length,
      includeCounts: options?.includeCounts
    });

    return teams;

  } catch (error) {
    logger.error('Failed to get organization teams:', error);
    // Opt-in only: default behavior (swallow to []) is preserved for every
    // existing caller; throwOnError lets pickers distinguish failure from a
    // genuinely team-less gym.
    if (options?.throwOnError) {
      throw error;
    }
    return [];
  }
}

/**
 * Get organization team IDs (lightweight)
 * 
 * Use when you only need team IDs for filtering other queries
 * 
 * @param organizationId - The organization ID
 * @param activeOnly - Whether to include only active teams (default: true)
 * @returns Array of team IDs
 */
export async function getOrganizationTeamIds(
  organizationId: string,
  activeOnly: boolean = true
): Promise<string[]> {
  try {
    logger.info('Fetching organization team IDs:', { organizationId, activeOnly });

    let query = supabase
      .from('teams')
      .select('id')
      .eq('organization_id', organizationId); // Multi-tenant isolation

    if (activeOnly) {
      query = query.eq('status', 'active');
    }

    const { data, error } = await query;

    if (error) {
      logger.error('Error fetching organization team IDs:', error);
      throw error;
    }

    const teamIds = data?.map(team => team.id) || [];
    
    logger.info(`Found ${teamIds.length} team IDs in organization`, { organizationId, activeOnly });
    
    return teamIds;

  } catch (error) {
    logger.error('Failed to get organization team IDs:', error);
    return [];
  }
}

/**
 * Get organization team count (efficient count query)
 * 
 * @param organizationId - The organization ID
 * @param options - Optional filters
 * @returns Number of teams in organization
 */
export async function getOrganizationTeamCount(
  organizationId: string,
  options?: {
    activeOnly?: boolean;
    seasonId?: string;
    programId?: string;
  }
): Promise<number> {
  try {
    let query = supabase
      .from('teams')
      .select('*', { count: 'exact', head: true })
      .eq('organization_id', organizationId); // Multi-tenant isolation

    if (options?.activeOnly !== false) {
      query = query.eq('status', 'active');
    }

    if (options?.seasonId) {
      query = query.eq('season_id', options.seasonId);
    }

    if (options?.programId) {
      query = query.eq('program_id', options.programId);
    }

    const { count, error } = await query;

    if (error) {
      logger.error('Error getting organization team count:', error);
      return 0;
    }

    logger.info(`Organization has ${count || 0} teams`, { organizationId, options });
    
    return count || 0;

  } catch (error) {
    logger.error('Failed to get organization team count:', error);
    return 0;
  }
}

/**
 * Get teams by coach/staff member
 * 
 * Helper function to get teams assigned to a specific staff member
 * 
 * @param organizationId - The organization ID
 * @param staffId - The organization_staff ID (NOT auth_user_id)
 * @param options - Optional filters
 * @returns Array of teams assigned to staff member
 */
export async function getStaffAssignedTeams(
  organizationId: string,
  staffId: string,
  options?: {
    activeOnly?: boolean;
    includeAssistantCoaches?: boolean;
  }
): Promise<Team[]> {
  try {
    logger.info('Fetching teams assigned to staff:', { organizationId, staffId, options });

    let query = supabase
      .from('teams')
      .select('*')
      .eq('organization_id', organizationId); // Multi-tenant isolation

    // Apply status filter
    if (options?.activeOnly !== false) {
      query = query.eq('status', 'active');
    }

    const { data: allTeams, error } = await query;

    if (error) {
      logger.error('Error fetching teams for staff assignment check:', error);
      throw error;
    }

    // Filter teams where staff is head coach or assistant coach
    const assignedTeams = allTeams?.filter(team => {
      const isHeadCoach = team.coach_id === staffId;
      const isAssistantCoach = options?.includeAssistantCoaches && 
                              team.assistant_coach_ids && 
                              Array.isArray(team.assistant_coach_ids) && 
                              team.assistant_coach_ids.includes(staffId);
      
      return isHeadCoach || isAssistantCoach;
    }) || [];

    logger.info(`Found ${assignedTeams.length} teams assigned to staff`, { 
      organizationId, 
      staffId,
      teamCount: assignedTeams.length,
      includeAssistant: options?.includeAssistantCoaches
    });

    return assignedTeams;

  } catch (error) {
    logger.error('Failed to get staff assigned teams:', error);
    return [];
  }
}
